import React, { useRef, useState } from 'react';
import { X, ShoppingCart, Minus, Plus, Trash2, ArrowRight, Package, CreditCard } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import './CartDrawer.css';

const API_ROOT = process.env.REACT_APP_API_URL || (window.location.port === '3000'
  ? '/api'
  : `${process.env.PUBLIC_URL || ''}/api`);

export default function CartDrawer({ isOpen, onClose, onOrderPlaced, onRequireAuth }) {
  const { items, removeItem, updateQty, clearCart, total, count } = useCart();
  const { token } = useAuth();
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  const [paymentMode, setPaymentMode] = useState('card');
  const overlayRef = useRef();
  const placingRef = useRef(false);
  const createdOrderIdRef = useRef(null);

  const handleOverlay = (e) => { if (e.target === overlayRef.current) onClose(); };

  const handlePlaceOrder = async () => {
    if (placingRef.current) return;
    setError('');
    if (!token) {
      if (onRequireAuth) {
        onClose(); // Close the cart drawer
        onRequireAuth(); // Open the auth modal
      } else {
        setError('Please log in to place an order.');
      }
      return;
    }
    setPlacing(true);
    placingRef.current = true;
    try {
      let orderId = createdOrderIdRef.current;
      
      if (!orderId) {
        const res = await fetch(`${API_ROOT}/orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ items, note: paymentMode === 'net30' ? 'Net-30 terms requested.' : '' }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || 'Order failed.');
        
        orderId = data.order_id;
        createdOrderIdRef.current = orderId;
      }

      if (paymentMode === 'net30') {
        clearCart();
        createdOrderIdRef.current = null;
        onClose();
        if (onOrderPlaced) onOrderPlaced(orderId);
        return;
      }

      const checkoutRes = await fetch(`${API_ROOT}/stripe/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ order_id: orderId, payment_method: 'card' }),
      });
      const checkoutData = await checkoutRes.json();
      if (!checkoutData.success) {
        throw new Error(checkoutData.message || 'Stripe checkout could not be started.');
      }

      clearCart();
      createdOrderIdRef.current = null;
      onClose();
      if (checkoutData.checkout_url) {
        window.location.href = checkoutData.checkout_url;
      } else {
        if (onOrderPlaced) onOrderPlaced(orderId);
      }
    } catch (err) {
      setError(err.message || 'Payment could not be completed.');
    } finally {
      setPlacing(false);
      placingRef.current = false;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        ref={overlayRef}
        className={`cart-backdrop ${isOpen ? 'open' : ''}`}
        onClick={handleOverlay}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside className={`cart-drawer ${isOpen ? 'open' : ''}`} aria-label="Shopping cart">
        {/* Header */}
        <div className="cart-header">
          <div className="cart-header-left">
            <ShoppingCart size={18} />
            <span>Your Cart</span>
            {count > 0 && <span className="cart-count-badge">{count}</span>}
          </div>
          <button className="cart-close-btn" onClick={onClose} aria-label="Close cart"><X size={18} /></button>
        </div>

        {/* Body */}
        <div className="cart-body">
          {items.length === 0 ? (
            <div className="cart-empty">
              <div className="cart-empty-icon"><Package size={40} strokeWidth={1.3} /></div>
              <h3>Your cart is empty</h3>
              <p>Add healthcare linen products to get started.</p>
            </div>
          ) : (
            <div className="cart-items">
              {items.map((item) => (
                <div className="cart-item" key={item.name}>
                  {item.image && (
                    <img src={item.image} alt={item.name} className="cart-item-img" />
                  )}
                  {!item.image && (
                    <div className="cart-item-img-placeholder">
                      <Package size={20} strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="cart-item-info">
                    <p className="cart-item-name">{item.name}</p>
                    <p className="cart-item-price">{item.price || 'Price on request'}</p>
                    <div className="cart-qty-row">
                      <button
                        className="cart-qty-btn"
                        onClick={() => updateQty(item.name, item.qty - 1)}
                        aria-label="Decrease quantity"
                      ><Minus size={12} /></button>
                      <span className="cart-qty-num">{item.qty}</span>
                      <button
                        className="cart-qty-btn"
                        onClick={() => updateQty(item.name, item.qty + 1)}
                        aria-label="Increase quantity"
                      ><Plus size={12} /></button>
                    </div>
                  </div>
                  <button
                    className="cart-remove-btn"
                    onClick={() => removeItem(item.name)}
                    aria-label={`Remove ${item.name}`}
                  ><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="cart-footer">
            <div className="cart-total-row">
              <span>Estimated Total</span>
              <span className="cart-total-amount">${total.toFixed(2)}</span>
            </div>
            <p className="cart-total-note">Taxes & shipping calculated at checkout</p>
            {error && <p className="cart-error">{error}</p>}
            <div className="cart-payment-options" role="group" aria-label="Payment options">
              <button
                type="button"
                className={`cart-payment-option ${paymentMode === 'card' ? 'active' : ''}`}
                onClick={() => setPaymentMode('card')}
              >
                <CreditCard size={14} /> Card payment
              </button>
              <button
                type="button"
                className={`cart-payment-option ${paymentMode === 'net30' ? 'active' : ''}`}
                onClick={() => setPaymentMode('net30')}
              >
                <Package size={14} /> Net-30 terms
              </button>
            </div>
            <button
              className="cart-place-order-btn"
              onClick={handlePlaceOrder}
              disabled={placing}
            >
              {placing
                ? <span className="cart-spinner" />
                : <><ArrowRight size={16} /> {paymentMode === 'net30' ? 'Create order' : 'Pay with card'}</>}
            </button>
            <button className="cart-clear-btn" onClick={clearCart}>Clear cart</button>
          </div>
        )}
      </aside>
    </>
  );
}
